export { ConnectionStatus } from '@/shared/components/feedback/ConnectionStatus'
import { BaseApiClient } from './clients/base-client'
import { TradingApiClient } from './clients/trading-client'
import { AIModelsApiClient } from './clients/ai-models-client'
import { AuthApiClient } from './clients/auth-client'
import { MarketDataApiClient } from './clients/market-data-client'
import { RiskManagementApiClient } from './clients/risk-management-client'
import { PortfolioApiClient } from './clients/portfolio-client'
import { DataQualityApiClient } from './clients/data-quality-client'
import { AITradingApiClient } from './clients/ai-trading-client'
import { ChatAIClient } from './clients/chat-ai-client'
import { ArbitrageApiClient } from './clients/arbitrage-client'
import { BrokerApiClient } from './clients/broker-client'
import { ConnectionsApiClient } from './clients/connections-client'

export { BaseApiClient } from './clients/base-client'
export { TradingApiClient } from './clients/trading-client'
export { AIModelsApiClient } from './clients/ai-models-client'
export { AuthApiClient } from './clients/auth-client'
export { MarketDataApiClient } from './clients/market-data-client'
export { RiskManagementApiClient } from './clients/risk-management-client'
export { PortfolioApiClient } from './clients/portfolio-client'
export { DataQualityApiClient } from './clients/data-quality-client'
export { AITradingApiClient } from './clients/ai-trading-client'
export { ChatAIClient } from './clients/chat-ai-client'
export { ArbitrageApiClient } from './clients/arbitrage-client'
export { BrokerApiClient } from './clients/broker-client'
export { ConnectionsApiClient } from './clients/connections-client'
export * from './clients/config'
export type {
  BrokerConnectionInfo,
  BrokersListResponse,
  RobinhoodAgenticStatus,
  BrokerConnectionType,
} from './clients/broker-client'
export type {
  PublicConnection,
  ConnectionAuthType,
  ConnectionLifecycleStatus,
  ConnectionEnvironment,
  CreateConnectionBody,
  CredentialsResult,
  ConnectionWorkspaceSnapshot,
  WorkspaceSectionStatus,
  ConnectionEquityHistory,
  EquityHistoryPoint,
  EquityHistoryRange,
  EquityHistoryStatus,
} from './clients/connections-client'

// Trading types
export type { Order, Position, Portfolio, Analytics, CreateOrderRequest } from './clients/trading-client'
// AI Models types
export type { AIModel, TrainingJob, Prediction, Feature, CreateModelRequest, TrainingRequest, PredictionRequest } from './clients/ai-models-client'
// Auth types
export type { User, LoginRequest, RegisterRequest, LoginResponse, MFAVerifyRequest, ChangePasswordRequest } from './clients/auth-client'

// Secure Profile and Access Link types
export interface SecureProfileResponse {
  profile: any;
  allowedFields?: string[];
}

export interface AccessLinkRequest {
  targetUserId: string;
  resourceType: string;
  permissions?: string[];
  expiresInMinutes?: number;
  purpose?: string;
}

export interface AccessLinkResponse {
  accessLink: {
    id: string;
    accessUrl: string;
    targetUser: any;
    resourceType: string;
    permissions: string[];
    expiresAt: string;
    purpose: string;
  };
}

export interface ValidateAccessResponse {
  data: any;
  accessLink: {
    id: string;
    resourceType: string;
    permissions: string[];
    expiresAt: string;
    purpose: string;
  };
}
// Market Data types
export type {
  OHLCVData,
  OHLCVResponse,
  TickerData,
  MarketDataContext,
  MarketDataDelayClass,
  DataSource,
  TechnicalIndicator,
  MarketAnalysis,
  SymbolInfo,
  ListedSymbol,
  ListedSymbolSearch,
} from './clients/market-data-client'
// Risk Management types
export type { RiskLimit, RiskAssessment, PositionRisk, RiskMonitoring, RiskAlert, RiskViolation } from './clients/risk-management-client'
// Portfolio types
export type { Position as PortfolioPosition, PortfolioOverview, PerformanceMetrics, AllocationData, Trade } from './clients/portfolio-client'
// Data Quality types
export type { DataQualityValidationResult, DataQualityMetrics, DataQualityReport, DataQualityRule, DataQualityAlert } from './clients/data-quality-client'
// AI Trading types
export type { AITradingAgent, AITradingSignal, AIStrategy, CreateAgentRequest, AgentControlAction } from './clients/ai-trading-client'
// Chat AI types
export type { ChatMessage, ChatConversation, SendMessageRequest, ChatAnalysis } from './clients/chat-ai-client'
// Arbitrage types
export type { ArbitrageOpportunity, ArbitrageExecution, ArbitrageSettings, ExchangeStatus } from './clients/arbitrage-client'

import API_CONFIG from '@/infrastructure/config/api'

export class AppApiClient {
  private tradingClient: TradingApiClient
  private aiModelsClient: AIModelsApiClient
  private authClient: AuthApiClient
  private marketDataClient: MarketDataApiClient
  private riskManagementClient: RiskManagementApiClient
  private portfolioClient: PortfolioApiClient
  private dataQualityClient: DataQualityApiClient
  private aiTradingClient: AITradingApiClient
  private chatAIClient: ChatAIClient
  private arbitrageClient: ArbitrageApiClient
  private brokerClient: BrokerApiClient
  private connectionsClient: ConnectionsApiClient
  private baseClient: BaseApiClient

  constructor(config?: any) {
    const apiConfig = config || API_CONFIG
    this.tradingClient = new TradingApiClient(apiConfig)
    this.aiModelsClient = new AIModelsApiClient(apiConfig)
    this.authClient = new AuthApiClient(apiConfig)
    this.marketDataClient = new MarketDataApiClient(apiConfig)
    this.riskManagementClient = new RiskManagementApiClient(apiConfig)
    this.portfolioClient = new PortfolioApiClient(apiConfig)
    this.dataQualityClient = new DataQualityApiClient(apiConfig)
    this.aiTradingClient = new AITradingApiClient(apiConfig)
    this.chatAIClient = new ChatAIClient(apiConfig)
    this.arbitrageClient = new ArbitrageApiClient(apiConfig)
    this.brokerClient = new BrokerApiClient(apiConfig)
    this.connectionsClient = new ConnectionsApiClient(apiConfig)
    this.baseClient = new BaseApiClient(apiConfig)
  }

  setAuthToken(token: string): void {
    this.tradingClient.setAuthToken(token)
    this.aiModelsClient.setAuthToken(token)
    this.authClient.setAuthToken(token)
    this.marketDataClient.setAuthToken(token)
    this.riskManagementClient.setAuthToken(token)
    this.portfolioClient.setAuthToken(token)
    this.dataQualityClient.setAuthToken(token)
    this.aiTradingClient.setAuthToken(token)
    this.chatAIClient.setAuthToken(token)
    this.arbitrageClient.setAuthToken(token)
    this.brokerClient.setAuthToken(token)
    this.connectionsClient.setAuthToken(token)
    this.baseClient.setAuthToken(token)
  }

  clearAuthToken(): void {
    this.tradingClient.clearAuthToken()
    this.aiModelsClient.clearAuthToken()
    this.authClient.clearAuthToken()
    this.marketDataClient.clearAuthToken()
    this.riskManagementClient.clearAuthToken()
    this.portfolioClient.clearAuthToken()
    this.dataQualityClient.clearAuthToken()
    this.aiTradingClient.clearAuthToken()
    this.chatAIClient.clearAuthToken()
    this.arbitrageClient.clearAuthToken()
    this.brokerClient.clearAuthToken()
    this.connectionsClient.clearAuthToken()
    this.baseClient.clearAuthToken()
  }

  get trading() { return this.tradingClient }
  get aiModels() { return this.aiModelsClient }
  get auth() { return this.authClient }
  get marketData() { return this.marketDataClient }
  get riskManagement() { return this.riskManagementClient }
  get portfolio() { return this.portfolioClient }
  get dataQuality() { return this.dataQualityClient }
  get aiTrading() { return this.aiTradingClient }
  get chatAI() { return this.chatAIClient }
  get arbitrage() { return this.arbitrageClient }
  get brokers() { return this.brokerClient }
  get connections() { return this.connectionsClient }
  get system() { return this.baseClient }

  async healthCheck() { return this.baseClient.healthCheck() }
  async getSystemInfo() { return this.baseClient.getSystemInfo() }
}
export const appApi = new AppApiClient()
export default appApi
export * from './ApiContext'
export * from './use-api'
